// Ref: GitHub issue #37, task A2 — route-level code splitting
import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { AdminRoute } from './components/layout/AdminRoute';
import { PageSkeleton } from './components/layout/PageSkeleton';

// Lazy-load all page components so each route becomes its own chunk.
// This targets ~60% reduction in the initial bundle.
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const JobsPage = lazy(() => import('./pages/JobsPage').then((m) => ({ default: m.JobsPage })));
const JobDetailPage = lazy(() => import('./pages/JobDetailPage').then((m) => ({ default: m.JobDetailPage })));
const CustomersPage = lazy(() => import('./pages/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => import('./pages/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })));
const CustomerFormPage = lazy(() => import('./pages/CustomerFormPage').then((m) => ({ default: m.CustomerFormPage })));
const JobFormPage = lazy(() => import('./pages/JobFormPage').then((m) => ({ default: m.JobFormPage })));
const ProductsPage = lazy(() => import('./pages/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage').then((m) => ({ default: m.OrgSettingsPage })));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage').then((m) => ({ default: m.UserManagementPage })));
const SchedulingPage = lazy(() => import('./pages/SchedulingPage').then((m) => ({ default: m.SchedulingPage })));

/** Wrap a route element so it suspends with the page skeleton while its chunk loads. */
function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageSkeleton />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
  },
  {
    path: '/',
    element: <Navigate to="/jobs" replace />,
  },
  {
    path: '/jobs',
    element: (
      <ProtectedRoute>
        {withSuspense(<JobsPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs/new',
    element: (
      <ProtectedRoute>
        {withSuspense(<JobFormPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/jobs/:id',
    element: (
      <ProtectedRoute>
        {withSuspense(<JobDetailPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers',
    element: (
      <ProtectedRoute>
        {withSuspense(<CustomersPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/new',
    element: (
      <ProtectedRoute>
        {withSuspense(<CustomerFormPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:id',
    element: (
      <ProtectedRoute>
        {withSuspense(<CustomerDetailPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:id/edit',
    element: (
      <ProtectedRoute>
        {withSuspense(<CustomerFormPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/products',
    element: (
      <ProtectedRoute>
        {withSuspense(<ProductsPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <AdminRoute>
          {withSuspense(<OrgSettingsPage />)}
        </AdminRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/org/users',
    element: (
      <ProtectedRoute>
        <AdminRoute>
          {withSuspense(<UserManagementPage />)}
        </AdminRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/scheduling',
    element: (
      <ProtectedRoute>
        {withSuspense(<SchedulingPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/jobs" replace />,
  },
]);
